/**
 * Helpers for Shift72 TVOD player embedding.
 *
 * The Shift72 player is embedded via iframe on detail pages when a user
 * navigates with a `?play=film/1234` query parameter. This happens after
 * a TVOD purchase (Shift72 redirects back to justwatch.com with the param)
 * or from transactional emails.
 *
 * Shift72 player docs: https://apidocs.shift72.com/player/
 */

function getShift72PlayerDomain(): string {
	if (JW_CONFIG.DOMAIN === 'moviecycle.com') {
		return 'staging-justwatch.shift72.com';
	}
	return 'tv.justwatch.com';
}

/**
 * Build the full iframe URL for the Shift72 player.
 *
 * @param slug - Shift72 content slug, e.g. "film/1234"
 * @param authToken - Optional Shift72 auth token for entitled playback
 */
export function getShift72PlayerUrl(slug: string, authToken?: string): string {
	const params = new URLSearchParams({
		nav: 'false',
		from: '',
		airplay: 'false',
		cast: 'false',
	});

	if (authToken) {
		params.set('u', authToken);
	}

	const normalizedSlug = slug.startsWith('/') ? slug : `/${slug}`;
	return `https://${getShift72PlayerDomain()}/play/#${normalizedSlug}?${params.toString()}`;
}

/**
 * Extract the `?play=` query parameter from the current route.
 *
 * @returns The Shift72 slug (e.g. "film/1234") or null if not present
 */
export function getPlaySlugFromQuery(routerQuery: Record<string, string>): string | null {
	return routerQuery['play'] ?? null;
}

/**
 * Get the origin URL for the Shift72 player domain (for postMessage origin checks).
 */
export function getShift72PlayerOrigin(): string {
	return `https://${getShift72PlayerDomain()}`;
}
